import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useSavedSessions } from '../hooks/useSavedSessions';
import {
  FavoritesView,
  UpgradePrompt,
} from '../components';
import { NameResult } from '../types/NameResult';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useCreditsContext } from '../context/CreditsContext';
import api from '../services/api';
import { Sidebar } from '../components/Sidebar';
import { LoadingOverlay } from '../components/LoadingOverlay';
import { ResultsPane } from '../components/ResultsPane';
import { TopNav } from '../components/TopNav';
import posthog from 'posthog-js';

export default function NameGenerator() {
  const [results, setResults] = useState<NameResult[]>([]);
  const [keywords, setKeywords] = useState('');
  const [tone, setTone] = useState('professional');
  const [length, setLength] = useState('any');
  const [tlds, setTlds] = useState<string[]>(['.com']);
  const [strategy, setStrategy] = useState<string[]>([]);

  const [hasAutoGenerated, setHasAutoGenerated] = useState(false);
  const autoGenerateFiredRef = useRef(false);
  const [currentView, setCurrentView] = useState<'generate' | 'favorites'>(
    'generate',
  );

  const [loading, setLoading] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState<'credits_exhausted' | 'feature_locked'>('credits_exhausted');

  const location = useLocation();
  const search = location.search;
  const sessionId = new URLSearchParams(search).get('sid');

  const { isAuthenticated } = useAuth0();
  const { sessions, saveSession } = useSavedSessions();
  const { credits, consumeCredit } = useCreditsContext();

  const handleSaveSession = useCallback(() => {
    saveSession({
      keywords: keywords
        .split(',')
        .map((k) => k.trim())
        .filter(Boolean),
      tone,
      length,
      tlds,
      strategy,
      results: results,
    });
  }, [keywords, tone, length, tlds, strategy, results, saveSession]);

  const handleGenerate = useCallback(async () => {
    if (!keywords.trim()) {
      toast.error('Please enter some keywords');
      return;
    }

    // Check if user has credits
    if (credits === 0) {
      setUpgradeReason('credits_exhausted');
      setShowUpgradePrompt(true);
      posthog.capture('generation_blocked_no_credits', {
        keywords,
        tone,
        isAuthenticated
      });
      return;
    }

    // Consume a credit
    const consumed = consumeCredit();
    if (!consumed) {
      setUpgradeReason('credits_exhausted');
      setShowUpgradePrompt(true);
      return;
    }

    setLoading(true);

    setCurrentView('generate'); // Back to generate view

    try {
      const res = await api.generateNames({
        keywords: keywords
          .split(',')
          .map((k) => k.trim())
          .filter(Boolean),
        tone,
        length,
        tlds,
        naming_strategy: strategy.length > 0 ? strategy : undefined,
      });

      const names = res.data?.names || [];
      setResults(names);

      if (names.length === 0) {
        toast.error('No results found. Try different keywords.');
      } else {
        // Silent auto-save only for authenticated users
        if (isAuthenticated) {
          handleSaveSession();
        }

        posthog.capture('names_generated', {
          keywords,
          tone,
          length,
          tlds,
          strategy,
          resultCount: names.length,
          creditsRemaining: credits - 1,
          isAuthenticated
        });
      }
    } catch (err: any) {
      console.error('Generation failed:', err);
      toast.error(err.response?.data?.error || 'Failed to generate names');
    } finally {
      setLoading(false);
    }
  }, [keywords, credits, consumeCredit, tone, length, tlds, strategy, isAuthenticated, handleSaveSession]);

  // Handle session loading
  useEffect(() => {
    if (sessionId && sessions.length > 0) {
      const match = sessions.find((s) => s.id === sessionId);
      if (match) {
        setKeywords(match.keywords.join(', '));
        setTone(match.tone);
        setLength(match.length);
        setTlds(match.tlds ?? [(match as any).tld ?? '.com']);
        if (match.strategy) setStrategy(match.strategy);
        setResults(match.results);
      }
    }
  }, [sessionId, sessions]);

  // Handle router state from landing page and URL params for auto-generate
  useEffect(() => {
    if (hasAutoGenerated || sessionId) return;

    // Router state takes precedence (navigation from landing page)
    const state = location.state as { keywords?: string; tone?: string; tld?: string } | null;
    if (state?.keywords) {
      setKeywords(state.keywords);
      if (state.tone) setTone(state.tone);
      if (state.tld) setTlds([state.tld]);
      setHasAutoGenerated(true);
      window.history.replaceState(null, '', window.location.pathname + location.search);
      return;
    }

    // Fall back to URL query params
    const params = new URLSearchParams(location.search);
    const kw = params.get('keywords');
    const t = params.get('tone');
    if (kw) {
      setKeywords(kw);
      setTone(t || 'professional');
      setHasAutoGenerated(true);
    }
  }, [location.search, location.state, hasAutoGenerated, sessionId]);

  // Auto-generate when keywords are set from URL — fires at most once per page load
  useEffect(() => {
    if (hasAutoGenerated && keywords && !results.length && !loading) {
      if (autoGenerateFiredRef.current) return;
      autoGenerateFiredRef.current = true;
      handleGenerate();
    }
  }, [hasAutoGenerated, keywords, handleGenerate, loading, results.length]);

  const handleBackToResults = () => {
    setCurrentView('generate');
  };

  const handleExampleClick = (exampleKeywords: string) => {
    setKeywords(exampleKeywords);
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-[#09090f]">
      {/* Upgrade Prompt Modal */}
      <UpgradePrompt
        isOpen={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        reason={upgradeReason}
        feature={upgradeReason === 'feature_locked' ? 'save favorites' : undefined}
      />

      <TopNav />

      {/* Body: sidebar + results */}
      <div className="flex flex-1 overflow-hidden">

      {/* Left Sidebar */}
      <aside className="w-72 flex-shrink-0 border-r border-[#131325] overflow-y-auto flex flex-col">
        <Sidebar
          keywords={keywords}
          setKeywords={setKeywords}
          tone={tone}
          setTone={setTone}
          tlds={tlds}
          setTlds={setTlds}
          strategy={strategy}
          setStrategy={setStrategy}
          length={length}
          setLength={setLength}
          onGenerate={handleGenerate}
          loading={loading}
        />
      </aside>

      {/* Right Pane */}
      <div className="flex-1 flex flex-col overflow-hidden relative">
        <LoadingOverlay visible={loading} />

        {currentView === 'generate' ? (
          <ResultsPane
            results={results}
            loading={loading}
            onExampleClick={handleExampleClick}
            onDismiss={(name) => setResults((prev) => prev.filter((r) => r.name !== name))}
            selectedTlds={tlds}
          />
        ) : (
          <div className="flex-1 overflow-y-auto">
            <FavoritesView onBackToResults={handleBackToResults} />
          </div>
        )}
      </div>

      </div>
    </div>
  );
}
